import type { AnimationConnection, AnimationGraphNode, AnimationGraphVisualGroup } from "@/document/model/animation";
import { createEmptyAnimationGraph, normalizeAnimationGraph } from "@/document/model/animation";
import type { Command, CommandContext, CommandResult } from "./Command";

const EMPTY_RESULT: CommandResult = { changedNodeIds: [], affectedBounds: [] };

export class InsertAnimationNodeCommand implements Command {
  id = "animation.insertNode";
  label: string;

  constructor(private readonly node: AnimationGraphNode) {
    this.label = `Created ${node.name}`;
  }

  execute(context: CommandContext): CommandResult {
    graph(context).nodes.push(structuredClone(this.node));
    return EMPTY_RESULT;
  }

  undo(context: CommandContext): CommandResult {
    const animation = graph(context);
    animation.nodes = animation.nodes.filter(node => node.id !== this.node.id);
    animation.connections = animation.connections.filter(connection => connection.from.nodeId !== this.node.id && connection.to.nodeId !== this.node.id);
    return EMPTY_RESULT;
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class UpdateAnimationNodeCommand implements Command {
  id = "animation.updateNode";
  label: string;

  constructor(
    private readonly nodeId: string,
    private readonly before: AnimationGraphNode,
    private readonly after: AnimationGraphNode,
    label = "Updated animation node",
  ) {
    this.label = label;
  }

  execute(context: CommandContext): CommandResult {
    replaceNode(context, this.nodeId, this.after);
    return EMPTY_RESULT;
  }

  undo(context: CommandContext): CommandResult {
    replaceNode(context, this.nodeId, this.before);
    return EMPTY_RESULT;
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class MoveAnimationNodesCommand implements Command {
  id = "animation.moveNodes";
  label = "Moved animation nodes";

  constructor(
    private readonly before: Array<{ nodeId: string; position: { x: number; y: number } }>,
    private readonly after: Array<{ nodeId: string; position: { x: number; y: number } }>,
  ) {}

  execute(context: CommandContext): CommandResult {
    applyPositions(context, this.after);
    return EMPTY_RESULT;
  }

  undo(context: CommandContext): CommandResult {
    applyPositions(context, this.before);
    return EMPTY_RESULT;
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class DeleteAnimationNodesCommand implements Command {
  id = "animation.deleteNodes";
  label = "Deleted animation nodes";
  private removedConnections: AnimationConnection[] = [];
  private beforeVisualGroups: AnimationGraphVisualGroup[] = [];

  constructor(private readonly nodes: AnimationGraphNode[]) {}

  execute(context: CommandContext): CommandResult {
    const animation = graph(context);
    const nodeIds = new Set(this.nodes.map(node => node.id));
    this.removedConnections = animation.connections.filter(connection => nodeIds.has(connection.from.nodeId) || nodeIds.has(connection.to.nodeId));
    this.beforeVisualGroups = structuredClone(animation.visualGroups ?? []);
    animation.nodes = animation.nodes.filter(node => !nodeIds.has(node.id));
    animation.connections = animation.connections.filter(connection => !nodeIds.has(connection.from.nodeId) && !nodeIds.has(connection.to.nodeId));
    animation.visualGroups = (animation.visualGroups ?? [])
      .map(group => ({ ...group, nodeIds: group.nodeIds.filter(nodeId => !nodeIds.has(nodeId)) }))
      .filter(group => group.nodeIds.length > 0);
    return EMPTY_RESULT;
  }

  undo(context: CommandContext): CommandResult {
    const animation = graph(context);
    const existingIds = new Set(animation.nodes.map(node => node.id));
    for (const node of this.nodes) {
      if (!existingIds.has(node.id)) {
        animation.nodes.push(structuredClone(node));
      }
    }
    const connectionIds = new Set(animation.connections.map(connection => connection.id));
    for (const connection of this.removedConnections) {
      if (!connectionIds.has(connection.id)) {
        animation.connections.push(structuredClone(connection));
      }
    }
    animation.visualGroups = structuredClone(this.beforeVisualGroups);
    return EMPTY_RESULT;
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class UpsertAnimationConnectionCommand implements Command {
  id = "animation.upsertConnection";
  label = "Updated animation connection";
  private replaced?: AnimationConnection;

  constructor(private readonly connection: AnimationConnection) {}

  execute(context: CommandContext): CommandResult {
    const animation = graph(context);
    const nextConnection = connectionForUpsert(animation.nodes, animation.connections, this.connection);
    const targetIndex = animation.connections.findIndex(connection => sameEndpoint(connection.to, nextConnection.to));
    this.replaced = targetIndex >= 0 ? structuredClone(animation.connections[targetIndex]) : undefined;
    if (targetIndex >= 0) {
      animation.connections[targetIndex] = structuredClone(nextConnection);
    } else {
      animation.connections.push(structuredClone(nextConnection));
    }
    return EMPTY_RESULT;
  }

  undo(context: CommandContext): CommandResult {
    const animation = graph(context);
    animation.connections = animation.connections.filter(connection => connection.id !== this.connection.id);
    if (this.replaced) {
      animation.connections.push(structuredClone(this.replaced));
    }
    return EMPTY_RESULT;
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class DeleteAnimationConnectionsCommand implements Command {
  id = "animation.deleteConnections";
  label = "Deleted animation connections";

  constructor(private readonly connections: AnimationConnection[]) {}

  execute(context: CommandContext): CommandResult {
    const ids = new Set(this.connections.map(connection => connection.id));
    const animation = graph(context);
    animation.connections = animation.connections.filter(connection => !ids.has(connection.id));
    return EMPTY_RESULT;
  }

  undo(context: CommandContext): CommandResult {
    const animation = graph(context);
    const existingIds = new Set(animation.connections.map(connection => connection.id));
    for (const connection of this.connections) {
      if (!existingIds.has(connection.id)) {
        animation.connections.push(structuredClone(connection));
      }
    }
    return EMPTY_RESULT;
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class UpdateAnimationVisualGroupsCommand implements Command {
  id = "animation.updateVisualGroups";

  constructor(
    private readonly before: AnimationGraphVisualGroup[],
    private readonly after: AnimationGraphVisualGroup[],
    readonly label = "Updated animation groups",
  ) {}

  execute(context: CommandContext): CommandResult {
    graph(context).visualGroups = structuredClone(this.after);
    return EMPTY_RESULT;
  }

  undo(context: CommandContext): CommandResult {
    graph(context).visualGroups = structuredClone(this.before);
    return EMPTY_RESULT;
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

function graph(context: CommandContext) {
  context.document.animation = normalizeAnimationGraph(context.document.animation ?? createEmptyAnimationGraph());
  return context.document.animation;
}

function replaceNode(context: CommandContext, nodeId: string, nextNode: AnimationGraphNode): void {
  const animation = graph(context);
  const index = animation.nodes.findIndex(node => node.id === nodeId);
  if (index < 0) {
    throw new Error(`Animation node not found: ${nodeId}`);
  }
  animation.nodes[index] = structuredClone(nextNode);
}

function applyPositions(context: CommandContext, positions: Array<{ nodeId: string; position: { x: number; y: number } }>): void {
  const byId = new Map(positions.map(position => [position.nodeId, position.position] as const));
  for (const node of graph(context).nodes) {
    const position = byId.get(node.id);
    if (position) {
      node.position = { ...position };
    }
  }
}

function connectionForUpsert(nodes: AnimationGraphNode[], connections: AnimationConnection[], connection: AnimationConnection): AnimationConnection {
  if (!isMotionInputEndpoint(nodes, connection.to)) {
    return connection;
  }

  const requestedIndex = connection.to.index ?? 0;
  const occupied = connections.some(candidate =>
    candidate.id !== connection.id &&
    candidate.to.nodeId === connection.to.nodeId &&
    candidate.to.pin === "motionInput" &&
    endpointIndex(candidate.to) === requestedIndex
  );
  if (!occupied) {
    return { ...connection, to: { ...connection.to, index: requestedIndex } };
  }

  const used = new Set(
    connections
      .filter(candidate => candidate.to.nodeId === connection.to.nodeId && candidate.to.pin === "motionInput")
      .map(candidate => endpointIndex(candidate.to)),
  );
  let index = 0;
  while (used.has(index)) {
    index += 1;
  }
  return { ...connection, to: { ...connection.to, index } };
}

function isMotionInputEndpoint(nodes: AnimationGraphNode[], endpoint: AnimationConnection["to"]): boolean {
  return endpoint.pin === "motionInput" && nodes.some(node => node.id === endpoint.nodeId && node.kind === "motion");
}

function endpointIndex(endpoint: AnimationConnection["to"]): number {
  return endpoint.index ?? 0;
}

function sameEndpoint(left: AnimationConnection["to"], right: AnimationConnection["to"]): boolean {
  return left.nodeId === right.nodeId && left.pin === right.pin && endpointIndex(left) === endpointIndex(right);
}
